import React, { useState } from "react";
import {
  DndContext,
  useSensor,
  PointerSensor,
  closestCenter,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { Box, Paper, Typography } from "@mui/material";

type Option = {
  id: number;
  text: string;
};

type SortingQuestionProps = {
  question: string;
  sourceItems: Option[];
  onSubmit: (sortedItems: Option[]) => void;
};

export const SortingQuestion = ({
  question,
  sourceItems: initialSourceItems,
  onSubmit,
}: SortingQuestionProps) => {
  const [sourceItems, setSourceItems] = useState<Option[]>(initialSourceItems);
  const [targetOrder, setTargetOrder] = useState<Option[]>([]);

  const sensors = [useSensor(PointerSensor)];

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!over) return;

    if (
      sourceItems.some((item) => item.id === active.id) &&
      over.id === "target-container"
    ) {
      const movedItem = sourceItems.find((item) => item.id === active.id);
      setSourceItems((prev) => prev.filter((item) => item.id !== active.id));
      const newTargetOrder = [...targetOrder, movedItem!];
      setTargetOrder(newTargetOrder);
      onSubmit(newTargetOrder);
    }

    if (
      targetOrder.some((item) => item.id === active.id) &&
      over.id === "source-container"
    ) {
      const movedItem = targetOrder.find((item) => item.id === active.id);
      const newTargetOrder = targetOrder.filter(
        (item) => item.id !== active.id
      );
      setTargetOrder(newTargetOrder);
      setSourceItems((prev) => [...prev, movedItem!]);
      onSubmit(newTargetOrder);
    }
  };

  return (
    <Box>
      <Typography
        variant="body1"
        fontWeight={500}
        gutterBottom
        textAlign={"left"}
      >
        {question}
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Box
          sx={{
            display: "flex",
            gap: "20px",
            mt: 3,
            flexDirection: ["column-reverse", "row"],
          }}
        >
          <DroppableContainer id="source-container">
            {sourceItems.map((item) => (
              <DraggableItem key={item.id} id={item.id} text={item.text} />
            ))}
          </DroppableContainer>

          <DroppableContainer id="target-container">
            {targetOrder.map((item) => (
              <DraggableItem key={item.id} id={item.id} text={item.text} />
            ))}
          </DroppableContainer>
        </Box>
      </DndContext>
    </Box>
  );
};

const DroppableContainer = ({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        padding: 2,
        borderColor: isOver ? "primary.main" : "grey.400",
        borderRadius: 2,
        width: ["100%", "50%"],
        minHeight: 300,
        boxShadow: 3,
        backgroundColor: isOver ? "primary.light" : "grey.50",
      }}
    >
      {children}
    </Box>
  );
};

const DraggableItem = ({ id, text }: { id: number; text: string }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  return (
    <Paper
      ref={setNodeRef}
      sx={{
        transform: `translate3d(${transform?.x || 0}px, ${
          transform?.y || 0
        }px, 0)`,
        padding: "10px",
        border: "1px solid #ccc",
        borderRadius: "5px",
        background: "#fff",
        textAlign: "left",
        cursor: "grab",
        marginBottom: "10px",
        fontSize: "14px",
        color: "grey.600",
      }}
      {...attributes}
      {...listeners}
    >
      {text}
    </Paper>
  );
};
